import { View } from 'react-native';
import React, { useEffect, useRef, useState, useCallback, memo, forwardRef, useImperativeHandle } from 'react';
import { StyleSheet } from 'react-native-unistyles';

const DEFAULT_HEIGHT = 500;

/**
 * Web PaymentWebView — renders the Rainforest payment component in an iframe
 * and forwards `rainforest_payment_*` window messages to the parent.
 *
 * The iframe auto-sizes to its content height: payment.html (same-origin)
 * posts `rainforest_payment_height` messages, which drive `contentHeight`.
 * Until the first height report lands, `style.height` (or DEFAULT_HEIGHT) is used.
 */
const PaymentWebView = forwardRef(function PaymentWebView({ url, onSuccess, onError, onValid, onInvalid, style, autoSubmit = false }, ref) {
    const iframeRef = useRef(null);
    const [contentHeight, setContentHeight] = useState(null);

    useImperativeHandle(ref, () => ({
        submit: () => {
            if (iframeRef.current?.contentWindow) {
                iframeRef.current.contentWindow.postMessage({ type: 'rainforest_submit_payment' }, '*');
            }
        },
    }), []);

    useEffect(() => {
        const receiveMessage = (event) => {
            if (event.origin !== window.location.origin || event.source !== iframeRef.current?.contentWindow) return;
            const { type, detail } = event.data || {};
            if (!type) return;

            if (type === 'rainforest_payment_height') {
                const h = detail && typeof detail.height === 'number' ? detail.height : null;
                if (h && h > 0) setContentHeight(h);
                return;
            }

            console.log('[PaymentWebView.web] message:', type, detail);

            if (type === 'rainforest_payment_approved') {
                // approved = payin was successfully processed. The polling will
                // catch the GivingPayins record, but forward this for immediate feedback.
                onSuccess?.(detail);
            } else if (
                type === 'rainforest_payment_declined' ||
                type === 'rainforest_payment_error' ||
                type === 'webview_error'
            ) {
                onError?.(detail?.message || type);
            } else if (type === 'rainforest_payment_valid') {
                onValid?.(detail);
            } else if (type === 'rainforest_payment_invalid') {
                onInvalid?.(detail);
            }
        };

        window.addEventListener('message', receiveMessage);
        return () => window.removeEventListener('message', receiveMessage);
    }, [onSuccess, onError, onValid, onInvalid]);

    const handleLoad = useCallback(() => {
        if (autoSubmit && iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage({ type: 'rainforest_submit_payment' }, '*');
        }
    }, [autoSubmit]);

    const resolvedHeight = contentHeight ?? style?.height ?? DEFAULT_HEIGHT;

    return (
        <View style={[styles.container, style, { height: resolvedHeight }]} testID="payment-webview">
            <iframe
                ref={iframeRef}
                src={url}
                title="Payment"
                onLoad={handleLoad}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    backgroundColor: 'transparent',
                }}
            />
        </View>
    );
});

const styles = StyleSheet.create(() => ({
    container: {
        overflow: 'hidden',
        backgroundColor: 'transparent',
        position: 'relative',
    },
}));

export default memo(PaymentWebView);
